import { FC, useState, useEffect } from "react"
import { AsYouType, isValidPhoneNumber } from "libphonenumber-js"
import {
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  makeStyles,
  Typography,
} from "@material-ui/core"
import clsx from "clsx"
import { colors } from "../../utils/theme"
import { GhostButton, PrimaryButton } from "../atoms/button/Button"
import { DefaultCountryCode, DefaultCountry } from "../../utils/constants"
import Input from "../atoms/input/Input"

interface MobileUpdateDialogProps extends DialogProps {
  mobile: string
  closeCallback: () => void
  openMobileVerifyDialog: (mobile: string) => void
}

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: 5,
    right: 0,
    padding: theme.spacing(2),
  },
  title: {
    padding: theme.spacing(4, 4, 2),
  },
  titleContent: {
    fontSize: 30,
    color: colors.neutral.denim,
  },
  contentRoot: {
    padding: theme.spacing(1, 4, 4),
  },
  description: {
    color: colors.neutral.denim,
    fontSize: 15,
  },
  input: {
    width: "100%",
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(5),
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  button: {
    width: "100%",
    fontSize: 20,
  },
  primaryButton: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(4),
  },
}))

const MobileUpdateDialog: FC<MobileUpdateDialogProps> = ({
  mobile,
  closeCallback,
  openMobileVerifyDialog,
  ...props
}) => {
  const classes = useStyles()
  const [updatePhone, setUpdatePhone] = useState<string>("")
  const [mobileError, setMobileError] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (props.open && mobile) {
      setUpdatePhone(mobile)
    }
  }, [mobile, props.open])

  const submit = () => {
    setMobileError(undefined)
    if (updatePhone) {
      if (
        !isValidPhoneNumber(
          DefaultCountryCode + " " + updatePhone,
          DefaultCountry
        )
      ) {
        setMobileError("Please enter a valid mobile number.")
        return
      }
    }
    openMobileVerifyDialog(updatePhone)
  }

  return (
    <Dialog {...props} fullWidth maxWidth="xs">
      <DialogTitle disableTypography className={classes.title}>
        <Typography
          variant="h3"
          component="h2"
          className={classes.titleContent}
        >
          Update mobile number
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.contentRoot}>
        <Typography variant="body1" className={classes.description}>
          Provide your mobile number to receive a text message if Vira detects
          an assessment response outside the risk threshold for one of your
          clients.
        </Typography>
        <Input
          label="Mobile Number"
          placeholder="Mobile Number"
          value={updatePhone}
          onChange={(e) => {
            const formatted = new AsYouType({
              defaultCallingCode: DefaultCountryCode,
            }).input(e.target.value)
            setUpdatePhone(formatted)
          }}
          error={Boolean(mobileError)}
          helperText={mobileError}
          className={clsx(classes.input)}
        />
        <div className={clsx(classes.primaryButton, classes.center)}>
          <PrimaryButton onClick={submit} className={classes.button}>
            Save
          </PrimaryButton>
        </div>
        <div className={classes.center}>
          <GhostButton
            onClick={(e) => {
              closeCallback()
            }}
          >
            Do not update mobile number
          </GhostButton>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default MobileUpdateDialog
